"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { getAnonymousUser } from "~/services/api/services/users";
import { UserIdContext } from "./user-id-context";
import { STORAGE_KEYS } from "~/types/Storage";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";

function isValidUserId(value: string): boolean {
    if (!value || value === "undefined" || value.startsWith("v1:")) return false;
    return true;
}

export const UserIdProvider = ({ children }: { children: ReactNode }) => {
    const [userId, setUserId] = useState<string | null>(() => {
        const stored = getStorageItem(STORAGE_KEYS.USER_ID);
        return isValidUserId(stored) ? stored : getAnonymousUser();
    });
    const [isLoading, setIsLoading] = useState(() => !userId);

    // Ref breaks the circular dependency: handleSetUserId → resetUserId → handleSetUserId
    const resetUserIdRef = useRef<() => void>(() => {});

    const handleSetUserId = useCallback((newUserId: string) => {
        if (!isValidUserId(newUserId)) {
            console.warn("Attempting to set undefined userId");
            resetUserIdRef.current();
            return;
        }

        setStorageItem(STORAGE_KEYS.USER_ID, newUserId);
        setUserId(newUserId);
    }, []);

    const resetUserId = useCallback(() => {
        setIsLoading(true);
        handleSetUserId(getAnonymousUser());
        setIsLoading(false);
    }, [handleSetUserId]);

    resetUserIdRef.current = resetUserId;

    useEffect(() => {
        const stored = getStorageItem(STORAGE_KEYS.USER_ID);
        if (userId && !isValidUserId(stored)) {
            handleSetUserId(userId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally including userId to run effect on its change
    }, [userId, handleSetUserId]);

    return (
        <UserIdContext.Provider
            value={{
                userId,
                isLoading,
                setUserId: handleSetUserId,
                resetUserId,
            }}
        >
            {children}
        </UserIdContext.Provider>
    );
};
